import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Reveal } from '@/components/motion';
import { CheckCircle, ArrowRight, ArrowLeft } from '@phosphor-icons/react';
import { public_api } from '@/lib/api';
import { SERVICES, CONTACT } from '@/data/site';

const STEPS = ['Trip details', 'Passenger', 'Options', 'Contact'];

export default function OnlineBooking() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    service_type: params.get('service') || 'NDIS Transport',
    pickup_address: '',
    dropoff_address: '',
    pickup_date: '',
    pickup_time: '',
    return_trip: false,
    return_time: '',
    passengers: 1,
    wheelchair_accessible: false,
    wheelchair_type: '',
    service_animal: false,
    carer_travelling: false,
    support_category: '',
    mobility_aids: '',
    support_worker_required: false,
    recurring: false,
    recurrence_pattern: '',
    recurrence_end_date: '',
    funding_source: 'NDIS',
    ndis_number: '',
    support_coordinator_name: '',
    support_coordinator_email: '',
    special_requirements: '',
    full_name: '',
    email: '',
    phone: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await public_api.booking({
        ...form,
        passengers: Number(form.passengers) || 1,
      });
      setDone(true);
      toast.success('Booking received — we\'ll confirm shortly.');
    } catch (err) {
      toast.error('Something went wrong. Please try again or call us.');
    } finally { setBusy(false); }
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (done) {
    return (
      <section className="pt-16 md:pt-24 pb-32" data-testid="booking-success">
        <div className="container-x max-w-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-8">
            <CheckCircle size={40} weight="fill" className="text-brand" />
          </div>
          <h1 className="h-section text-balance">Booking received.</h1>
          <p className="body-lg mt-6 text-pretty">
            Thank you {form.full_name}. Our team is confirming vehicle and driver availability now — expect a confirmation
            call or email within a few hours. For anything urgent, call us on <a href={`tel:${CONTACT.phone_dial}`} className="text-brand font-semibold">{CONTACT.phone_display}</a>.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/" className="btn-primary">Back to home</Link>
            <Link to="/services" className="btn-secondary">Explore services</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-16 md:pt-24 pb-24" data-testid="booking-page">
      <div className="container-x max-w-3xl">
        <Reveal>
          <div className="overline mb-6">Online booking</div>
          <h1 className="h-section text-balance">Book transport in under two minutes.</h1>
          <p className="body-md mt-4 text-pretty max-w-2xl">
            One-off, recurring or complex — this form supports it all. Prefer to call?
            {' '}<a href={`tel:${CONTACT.phone_dial}`} className="text-brand font-semibold">{CONTACT.phone_display}</a>{' '}(24/7).
          </p>
        </Reveal>

        <div className="mt-10">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s, i) => (
              <div key={s} className={`text-xs font-semibold uppercase tracking-widest ${i <= step ? 'text-brand' : 'text-ink-muted'}`}>
                {i + 1}. {s}
              </div>
            ))}
          </div>
          <div className="h-1 rounded-full bg-neutral-200 overflow-hidden">
            <div className="h-full bg-brand transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <form onSubmit={submit} className="mt-10 p-8 md:p-10 rounded-3xl bg-neutral-50 border border-neutral-200 space-y-6" data-testid="booking-form">
          {step === 0 && (
            <div className="space-y-5" data-testid="booking-step-0">
              <div>
                <label className="label-base">Service type</label>
                <select required value={form.service_type} onChange={e => set('service_type', e.target.value)} data-testid="booking-service" className="input-base">
                  {SERVICES.map(s => <option key={s.slug} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-base">Pickup address</label>
                <input required value={form.pickup_address} onChange={e => set('pickup_address', e.target.value)} data-testid="booking-pickup" className="input-base" placeholder="Street address, suburb, state" />
              </div>
              <div>
                <label className="label-base">Drop-off address</label>
                <input required value={form.dropoff_address} onChange={e => set('dropoff_address', e.target.value)} data-testid="booking-dropoff" className="input-base" placeholder="Destination address" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-base">Pickup date</label>
                  <input required type="date" value={form.pickup_date} onChange={e => set('pickup_date', e.target.value)} data-testid="booking-date" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Pickup time</label>
                  <input required type="time" value={form.pickup_time} onChange={e => set('pickup_time', e.target.value)} data-testid="booking-time" className="input-base" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input id="return_trip" type="checkbox" checked={form.return_trip} onChange={e => set('return_trip', e.target.checked)} data-testid="booking-return-toggle" className="w-4 h-4 accent-brand" />
                <label htmlFor="return_trip" className="text-ink font-medium text-sm">Include a return trip</label>
              </div>
              {form.return_trip && (
                <div>
                  <label className="label-base">Return pickup time</label>
                  <input type="time" value={form.return_time} onChange={e => set('return_time', e.target.value)} data-testid="booking-return-time" className="input-base" />
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="booking-step-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-base">Passengers</label>
                  <input required type="number" min="1" max="12" value={form.passengers} onChange={e => set('passengers', e.target.value)} data-testid="booking-passengers" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Funding source</label>
                  <select value={form.funding_source} onChange={e => set('funding_source', e.target.value)} data-testid="booking-funding" className="input-base">
                    <option>NDIS</option>
                    <option>TAC</option>
                    <option>WorkSafe</option>
                    <option>Aged Care (HCP / CHSP)</option>
                    <option>Private</option>
                    <option>Insurance</option>
                  </select>
                </div>
              </div>
              {form.funding_source === 'NDIS' && (
                <div>
                  <label className="label-base">NDIS number (optional)</label>
                  <input value={form.ndis_number} onChange={e => set('ndis_number', e.target.value)} data-testid="booking-ndis" className="input-base" />
                </div>
              )}
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-white hover:border-brand cursor-pointer">
                  <input type="checkbox" checked={form.wheelchair_accessible} onChange={e => set('wheelchair_accessible', e.target.checked)} data-testid="booking-wheelchair" className="w-4 h-4 accent-brand" />
                  <span className="font-medium text-ink">Wheelchair accessible vehicle required</span>
                </label>
                {form.wheelchair_accessible && (
                  <div>
                    <label className="label-base">Wheelchair type</label>
                    <select value={form.wheelchair_type} onChange={e => set('wheelchair_type', e.target.value)} data-testid="booking-wheelchair-type" className="input-base">
                      <option value="">Not sure</option>
                      <option>Manual wheelchair</option>
                      <option>Power wheelchair</option>
                      <option>Mobility scooter</option>
                      <option>Tilt-in-space wheelchair</option>
                    </select>
                  </div>
                )}
                <label className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-white hover:border-brand cursor-pointer">
                  <input type="checkbox" checked={form.support_worker_required} onChange={e => set('support_worker_required', e.target.checked)} data-testid="booking-support-worker" className="w-4 h-4 accent-brand" />
                  <span className="font-medium text-ink">Support worker travelling with participant</span>
                </label>
                <label className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-white hover:border-brand cursor-pointer">
                  <input type="checkbox" checked={form.carer_travelling} onChange={e => set('carer_travelling', e.target.checked)} data-testid="booking-carer" className="w-4 h-4 accent-brand" />
                  <span className="font-medium text-ink">Carer or family member travelling</span>
                </label>
                <label className="flex items-center gap-3 p-4 rounded-xl border border-neutral-200 bg-white hover:border-brand cursor-pointer">
                  <input type="checkbox" checked={form.service_animal} onChange={e => set('service_animal', e.target.checked)} data-testid="booking-service-animal" className="w-4 h-4 accent-brand" />
                  <span className="font-medium text-ink">Service animal travelling</span>
                </label>
              </div>
              {form.funding_source === 'NDIS' && (
                <div>
                  <label className="label-base">NDIS support category (optional)</label>
                  <select value={form.support_category} onChange={e => set('support_category', e.target.value)} data-testid="booking-support-category" className="input-base">
                    <option value="">Not sure</option>
                    <option>Core — Assistance with Daily Life</option>
                    <option>Core — Transport</option>
                    <option>Capacity Building — Increased Social & Community Participation</option>
                    <option>Capital Supports</option>
                  </select>
                </div>
              )}
              <div>
                <label className="label-base">Mobility aids or equipment</label>
                <input value={form.mobility_aids} onChange={e => set('mobility_aids', e.target.value)} data-testid="booking-aids" className="input-base" placeholder="e.g. walker, oxygen tank, power chair" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="booking-step-2">
              <div className="flex items-center gap-3">
                <input id="recurring" type="checkbox" checked={form.recurring} onChange={e => set('recurring', e.target.checked)} data-testid="booking-recurring" className="w-4 h-4 accent-brand" />
                <label htmlFor="recurring" className="text-ink font-medium text-sm">This is a recurring booking</label>
              </div>
              {form.recurring && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="label-base">Recurrence pattern</label>
                    <select value={form.recurrence_pattern} onChange={e => set('recurrence_pattern', e.target.value)} data-testid="booking-recurrence" className="input-base">
                      <option value="">Select…</option>
                      <option>weekly</option>
                      <option>fortnightly</option>
                      <option>monthly</option>
                      <option>custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-base">Recurrence end date (optional)</label>
                    <input type="date" value={form.recurrence_end_date} onChange={e => set('recurrence_end_date', e.target.value)} data-testid="booking-recurrence-end" className="input-base" />
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-base">Support coordinator name (optional)</label>
                  <input value={form.support_coordinator_name} onChange={e => set('support_coordinator_name', e.target.value)} data-testid="booking-sc-name" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Support coordinator email (optional)</label>
                  <input type="email" value={form.support_coordinator_email} onChange={e => set('support_coordinator_email', e.target.value)} data-testid="booking-sc-email" className="input-base" />
                </div>
              </div>
              <div>
                <label className="label-base">Special requirements or notes</label>
                <textarea rows={4} value={form.special_requirements} onChange={e => set('special_requirements', e.target.value)} data-testid="booking-notes" className="input-base resize-none" placeholder="Anything else our team should know." />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="booking-step-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-base">Your name</label>
                  <input required value={form.full_name} onChange={e => set('full_name', e.target.value)} data-testid="booking-name" className="input-base" />
                </div>
                <div>
                  <label className="label-base">Phone</label>
                  <input required value={form.phone} onChange={e => set('phone', e.target.value)} data-testid="booking-phone" className="input-base" placeholder="04XX XXX XXX" />
                </div>
              </div>
              <div>
                <label className="label-base">Email</label>
                <input required type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="booking-email" className="input-base" placeholder="you@email.com.au" />
              </div>
              <div className="rounded-xl bg-white border border-neutral-200 p-5 text-sm text-ink-soft">
                By submitting this booking you agree to Inclusive Mobility Group's{' '}
                <Link to="/terms" className="text-brand font-semibold underline">terms</Link> and{' '}
                <Link to="/privacy" className="text-brand font-semibold underline">privacy policy</Link>.
              </div>
            </div>
          )}

          <div className="pt-4 flex items-center justify-between border-t border-neutral-200">
            <button type="button" onClick={back} disabled={step === 0} data-testid="booking-back" className="btn-ghost disabled:opacity-30">
              <ArrowLeft size={16} weight="bold" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" onClick={next} data-testid="booking-next" className="btn-primary">
                Continue <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button type="submit" disabled={busy} data-testid="booking-submit" className="btn-primary">
                {busy ? 'Submitting…' : 'Submit booking'} <ArrowRight size={16} weight="bold" />
              </button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
